/**
 * Dock and Score Shortcut — a pre-LLM short-circuit for the canonical
 * "dock → minimize → score" workflow against known compounds.
 *
 * When the user says something like "dock and score these compounds" or
 * "evaluate binding against the target", we skip the LLM and construct a
 * fixed multi-step plan directly against whatever protein and compounds
 * are currently loaded in the workspace:
 *
 *   1. dock-compound             (autodock, accurate speed)
 *   2. energy-minimize           (flexible)
 *   3. get-energies              (binding energies for all poses)
 *   4. predict-gnina-scores      (GNINA CNN rescoring, skipDocking: true)
 *
 * **This is NOT hit discovery.** Hit discovery produces new compounds
 * from a library, fragment map, or generative model; this workflow
 * takes compounds the user already has and computes their binding
 * poses + scores. Honest medchem nomenclature: "dock and score" or
 * "binding pose evaluation". Real hit discovery lives in a separate
 * (as-yet-unbuilt) shortcut — see hitDiscoveryShortcut.js for the
 * roadmap.
 *
 * Why this exists as a shortcut: deterministic, zero-token, zero-latency
 * and zero-hallucination. A user with a protein + compounds loaded who
 * wants the standard dock/minimize/score pass should not have to wait
 * for an LLM round-trip to get back the same thing every time.
 *
 * @module gibbs/knowledge/dockAndScoreShortcut
 */

// ─── Phrase detection ──────────────────────────────────────────────────────

/**
 * Phrases that should trigger the dock-and-score shortcut.
 * Matched case-insensitively via substring search, so variations with
 * different capitalization, prefixes, or surrounding context still hit.
 *
 * Rule: a phrase belongs here only if it honestly describes docking and
 * scoring compounds that are already in the workspace. Phrases that
 * describe finding new compounds (hit discovery, virtual screening,
 * library screen, fragment search) must NOT be added here — those
 * belong to the real hit-discovery shortcut when it is built.
 */
// Keep this list TIGHT — only the canonical phrases a user would say
// for exactly this operation. Everything else falls through to the LLM,
// which has few-shot examples and the retry loop to handle fuzziness.
// Do NOT add every possible rephrase; the LLM handles that better than
// a substring list ever will.
const DOCK_AND_SCORE_PHRASES = [
    'dock and score',
    'dock & score',
    'rescore',
    'minimize and score',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchDockAndScoreIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return DOCK_AND_SCORE_PHRASES.some((p) => lower.includes(p));
}

// ─── Workspace precondition check ──────────────────────────────────────────

/**
 * Duck-typed check for "is a protein currently loaded in the workspace".
 * Uses the live App.Workspace API when available, falls back to a
 * serialized shape for tests, and finally falls back to optimism so
 * the downstream validator can produce the real error message if
 * we got it wrong.
 *
 * @param {Object} workspaceState
 * @returns {boolean}
 */
export function workspaceHasLoadedProtein(workspaceState) {
    if (!workspaceState) return false;

    // Live App.Workspace (what ChatBot.jsx passes in at runtime)
    if (typeof workspaceState.firstCaseData === 'function') {
        const caseData = workspaceState.firstCaseData();
        if (caseData == null) return false;
        if (typeof caseData.getSolute === 'function') {
            return caseData.getSolute() != null;
        }
        return true; // caseData exists, assume it's loaded
    }

    // Serialized workspace (tests / snapshots) — look for a protein field
    if (workspaceState.caseData || workspaceState.protein) return true;
    if (Array.isArray(workspaceState.proteins)
        && workspaceState.proteins.length > 0) return true;

    // Unknown shape — optimistic. If we're wrong, the workflow validator
    // will catch it with a precondition-unmet error.
    return true;
}

/**
 * Check whether a compound is loaded in the workspace. Docking + the
 * dock-and-score workflow are meaningless without at least one compound.
 */
export function workspaceHasLoadedCompound(workspaceState) {
    if (!workspaceState) return false;
    if (typeof workspaceState.getLoadedCompounds === 'function') {
        const compounds = workspaceState.getLoadedCompounds();
        return Array.isArray(compounds) && compounds.length > 0;
    }
    return true; // optimistic fallback
}

/**
 * Check whether the workspace has a defined binding site. Required
 * for docking to compute a non-degenerate bounding box. Without it
 * the BFD server returns "Inappropriate bounding box 0,0,0,0,0,0".
 */
export function workspaceHasBindingSite(workspaceState) {
    if (!workspaceState) return false;
    if (typeof workspaceState.getBindingSite === 'function') {
        const bs = workspaceState.getBindingSite();
        return bs != null;
    }
    return true; // optimistic fallback
}

// ─── Canonical workflow builder ────────────────────────────────────────────

/**
 * Build a workflow plan for the two supported phases:
 *
 * - **'acquire'** (used by the disease cascade's PDB-available branch):
 *   emits a single-step workflow that loads a protein from PDB. Nothing
 *   else. Use when the user picked a target from the cascade and hasn't
 *   yet brought compounds to the workspace. The workflow summary
 *   includes guidance telling them to load compounds and then say
 *   "dock and score".
 *
 * - **'dock_and_score'** (used by the dock-and-score shortcut): emits
 *   the dock → minimize → get-energies → predict-gnina-scores steps
 *   operating on whatever compounds and protein are already in the
 *   workspace. Assumes the user has completed the acquisition phase
 *   (a protein is loaded AND compounds are loaded AND a binding site
 *   is defined).
 *
 * The two phases are intentionally separated because mixing them makes
 * every workflow fragile: research PDB entries (like 6HTX) do not have
 * pre-associated starter compound libraries, so a mixed workflow that
 * tries to load starters + dock + score fails on the starters step
 * for anything other than curated target projects. By splitting
 * acquisition from scoring, we let the user control when each phase
 * fires and what compounds they want to work with.
 *
 * @param {Object} [options]
 * @param {'acquire' | 'dock_and_score'} [options.phase='acquire'] - Which workflow to emit
 * @param {string} [options.loadPdbId] - PDB ID for the load step (acquire phase only)
 * @param {string} [options.targetLabel] - Friendly label for the summary
 * @returns {Object} workflow-intent plan object
 */
export function buildDockAndScoreWorkflow({
    phase = 'acquire',
    loadPdbId = null,
    targetLabel = 'the active protein',
} = {}) {
    if (phase === 'acquire') {
        return buildAcquireStructureWorkflow({ loadPdbId, targetLabel });
    }
    return buildScoringWorkflow({ targetLabel });
}

/**
 * Acquisition phase — just load the protein. Fast, deterministic,
 * always works for a valid PDB ID.
 */
function buildAcquireStructureWorkflow({ loadPdbId, targetLabel }) {
    if (!loadPdbId) {
        return {
            intent: 'chat',
            message: 'I need a PDB ID to load a protein, but none was '
                + 'provided. Try specifying a PDB code directly.',
        };
    }
    return {
        intent: 'workflow',
        summary: `Load ${targetLabel} from PDB ${loadPdbId}. Once the `
            + 'structure is in the workspace, you can:\n'
            + '- Say **"discover hits"** to run fragment-based hit '
            + 'discovery (grow + minimize + score)\n'
            + '- Say **"dock and score"** if you already have compounds '
            + 'to evaluate',
        actions: [
            {
                actionId: 'load-protein-from-pdb',
                // pdbId is declared as an input in the registry, not a
                // parameter — it must go in resolvedInputs or the workflow
                // executor can't resolve it at runtime.
                params: {},
                resolvedInputs: { pdbId: loadPdbId },
                step: 1,
            },
        ],
    };
}

/**
 * Dock-and-score phase — dock → minimize → score (3 steps, not 4).
 * Assumes both protein and compounds are already in the workspace
 * and that a binding site has been detected or defined.
 *
 * Note: `get-energies` was dropped from this workflow in the
 * 2026-04-15 cleanup. The observation from jfreeze testing was that
 * Energy Minimization and Calculate Binding Energies appear
 * redundant to the user because both report an "energy" output on
 * similarly-labeled step cards. In fact energy-minimize already
 * returns updated energies as part of its normal output, so adding
 * a separate get-energies step computes the same numbers twice —
 * once on the minimized pose with the minimizer's cleanup cycle,
 * once via a standalone pass. Dropping get-energies simplifies the
 * UI narrative without losing information: the minimizer's energies
 * are still available to downstream steps and visible in the
 * Energies tab after minimization completes.
 */
function buildScoringWorkflow({ targetLabel }) {
    return {
        intent: 'workflow',
        summary: `Dock and score against ${targetLabel}: dock all loaded `
            + 'compounds, flexibly minimize the poses, and rescore with '
            + 'GNINA CNN.',
        actions: [
            {
                actionId: 'dock-compound',
                params: { dockingProgram: 'autodock', speedArg: 'accurate' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 1,
            },
            {
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 2,
            },
            {
                actionId: 'predict-gnina-scores',
                // skipDocking: true because step 1 (dock-compound) already
                // ran a full autodock pass. Without this flag, ScoreCompounds
                // would re-run diffdock + autodock internally before scoring,
                // wasting several minutes of compute on a redundant dock.
                // SME decision (jfreeze, 2026-04-15): no re-dock.
                params: { skipDocking: true },
                resolvedInputs: { compounds: 'all' },
                step: 3,
            },
        ],
    };
}

// ─── Top-level shortcut entry point ────────────────────────────────────────

/**
 * Try to resolve a dock-and-score intent into a workflow plan.
 * Returns one of:
 *   - { intent: 'workflow', ... } — ready to dispatch into the pipeline
 *   - { intent: 'chat', message } — user needs guidance (missing prereq)
 *   - null — the phrase did not match; caller should fall through
 *
 * @param {string} userMessage
 * @param {Object} workspaceState
 * @returns {Object|null}
 */
export function tryDockAndScoreShortcut(userMessage, workspaceState) {
    if (!matchDockAndScoreIntent(userMessage)) return null;

    if (!workspaceHasLoadedProtein(workspaceState)) {
        return {
            intent: 'chat',
            message: 'To dock and score I need a protein loaded in the'
                + ' workspace first. You can:\n\n'
                + '- Load a PDB structure by ID (e.g. *"load protein 4RMG"*)\n'
                + '- Upload a PDB/CIF file via the Import dialog\n'
                + '- Predict a structure via the Protein Folding tab\n\n'
                + "Once a protein is loaded, ask me again and I'll build the"
                + ' full dock → minimize → score workflow.',
        };
    }

    if (!workspaceHasLoadedCompound(workspaceState)) {
        return {
            intent: 'chat',
            message: 'To dock and score I need at least one compound'
                + ' loaded in the workspace. You can:\n\n'
                + '- Draw a compound in the sketcher (Draw / Edit → Draw New Cmpd)\n'
                + '- Paste SMILES or upload a mol/sdf file via the Import dialog\n'
                + '- Load starter compounds from a target project if one is'
                + ' configured\n\n'
                + "Once a compound is loaded, ask me again and I'll build the"
                + ' full dock → minimize → score workflow.',
        };
    }

    if (!workspaceHasBindingSite(workspaceState)) {
        return {
            intent: 'chat',
            message: 'The protein is loaded but no binding site is defined.'
                + ' Docking needs a bounding box around the target pocket,'
                + ' and without a binding site the server returns an empty'
                + ' box.\n\nTo define a binding site:\n\n'
                + '- Click on a residue or atom in the pocket, then use'
                + ' **Ligand View** on the right panel to focus BMaps on it\n'
                + '- Or position your compound inside the pocket first'
                + ' (docking will compute the box from the compound\'s'
                + ' current 3D position)\n'
                + '- Or load a PDB structure that has a co-crystallized'
                + ' ligand — the binding site is detected automatically\n\n'
                + "Once a binding site is set, ask me again and I'll build"
                + ' the dock-and-score workflow.',
        };
    }

    return buildDockAndScoreWorkflow({
        phase: 'dock_and_score',
        targetLabel: 'the active protein',
    });
}
